import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import { useTimer } from "../Context/TimerContext";
import { formatClock, formatDuration } from "../services/timerService";

/**
 * Start / Stop work-timer for a ticket, with a live ticking clock.
 *
 * All state comes from the app-wide TimerContext (one fetch, one ticker for
 * the whole app), so this component is a lightweight consumer safe to render
 * on every list card without extra network calls.
 *
 * Props:
 *   ticket   — the ticket object (needs id + status)
 *   compact  — smaller variant for list/card use
 *   onChange — optional callback(started: boolean) after a start/stop
 */

// Budget left at which the countdown starts warning rather than informing.
const BUDGET_WARN_SECONDS = 5 * 60;

const TicketTimerButton = ({ ticket, compact = false, onChange }) => {
  const timer = useTimer();
  const ticketId = ticket?.id;
  const status = ticket?.status;
  const isClosed = status === "resolved" || status === "rejected";
  const running = timer.isRunning?.(ticketId);

  // The server's "this budget is spent — start anyway?" question, held while
  // the person answers it. Null = nothing being asked.
  const [budgetAsk, setBudgetAsk] = useState(null);

  // Ticket got resolved/rejected while we still hold a running timer for it.
  //
  // Asks the server rather than just clearing the local clock: the resolve
  // stopped the session on the backend, and re-reading is what confirms the
  // minutes were banked. If the resolve came through a path that did not stop
  // it, the same read runs the policy sweep and stops it now.
  useEffect(() => {
    if (isClosed && running) timer.resync?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClosed, running]);

  if (isClosed || !ticketId) return null;
  const busy = timer.isBusy?.(ticketId);
  const otherActive = timer.hasActive && !running;
  const elapsed = running ? timer.elapsedSeconds?.() || 0 : 0;

  // The budget, but only for the ticket actually being timed: the number on
  // any other card is a static field the ticket already shows.
  //
  // Two states, not one — a running session can legitimately be PAST its
  // budget (started deliberately after the prompt below), and then the useful
  // number is how far past, not a countdown pinned at zero.
  const budgetLeft = running ? timer.budgetRemainingSeconds?.() : null;
  const budgetOver = running ? timer.budgetOverrunSeconds?.() : null;
  const budgetMinutes = running ? timer.budgetMinutes : null;
  const overBudget = budgetOver > 0;
  const budgetLow =
    !overBudget && budgetLeft !== null && budgetLeft <= BUDGET_WARN_SECONDS;

  const beginTimer = async (acknowledgeBudget = false) => {
    const resp = await timer.start?.(ticketId, { acknowledgeBudget });
    // Budget spent: the server refuses once and says so, and the answer is the
    // person's to give. Anything else the context surfaces as a notice.
    if (resp?.data?.budget_exhausted) {
      setBudgetAsk({
        message: resp.statusMessage,
        minutes: resp.data.time_budget_minutes,
        used: resp.data.logged_minutes,
      });
      return;
    }
    if (resp?.success) onChange?.(true);
  };

  const handleClick = async (e) => {
    e?.stopPropagation?.();
    if (busy) return;
    if (running) {
      await timer.stop?.();
      onChange?.(false);
    } else {
      await beginTimer(false);
    }
  };

  const confirmOverBudget = async () => {
    setBudgetAsk(null);
    await beginTimer(true);
  };

  const height = compact ? 28 : 36;
  const fontSize = compact ? 11 : 13;

  return (
    <Box
      sx={{ display: "inline-flex", flexDirection: "column", gap: 0.3 }}
      onClick={(e) => e.stopPropagation()}
    >
      <Box
        onClick={handleClick}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.5,
          height,
          minWidth: running ? (compact ? 78 : 96) : compact ? 58 : 74,
          px: compact ? 1 : 1.5,
          borderRadius: 1.5,
          cursor: busy ? "default" : "pointer",
          userSelect: "none",
          opacity: busy ? 0.7 : 1,
          border: running ? "1px solid #DC2626" : "1px solid #15803D",
          bgcolor: running ? "#FEE2E2" : "#DCFCE7",
          color: running ? "#DC2626" : "#15803D",
          transition: "all 0.15s ease",
        }}
      >
        {busy ? (
          <CircularProgress
            size={compact ? 12 : 16}
            thickness={5}
            color="inherit"
          />
        ) : running ? (
          <StopRoundedIcon sx={{ fontSize: compact ? 16 : 20 }} />
        ) : (
          <PlayArrowRoundedIcon sx={{ fontSize: compact ? 16 : 20 }} />
        )}
        <Typography sx={{ fontSize, fontWeight: 600, lineHeight: 1 }}>
          {running ? formatClock(elapsed) : "Start"}
        </Typography>
      </Box>

      {/* The budget line: a countdown to an automatic stop while there is
          budget left, and an overrun once there is not. */}
      {running && budgetLeft !== null && (
        <Typography
          sx={{
            fontSize: compact ? 9 : 10,
            fontWeight: 700,
            lineHeight: 1.2,
            color: overBudget ? "#B4413F" : budgetLow ? "#B45309" : "#6B7280",
            maxWidth: compact ? 96 : 170,
          }}
        >
          {overBudget
            ? `over budget by ${formatDuration(budgetOver)}`
            : `${formatDuration(budgetLeft)} left of ${budgetMinutes}m budget`}
        </Typography>
      )}

      {/* Hint when a timer is running on a DIFFERENT ticket */}
      {!compact && !running && otherActive && (
        <Typography sx={{ fontSize: 9, color: "#B45309", maxWidth: 160 }}>
          Running on {timer.activeTicketNumber || "another ticket"} — starting
          here will switch
        </Typography>
      )}

      {/* Why the clock went away, or why Start refused.
          Only on the detail variant: the notice is app-wide, and a list of
          fifty cards repeating it would be fifty copies of one sentence. */}
      {!compact && timer.notice && (
        <Typography
          onClick={() => timer.clearNotice?.()}
          sx={{
            fontSize: 10,
            fontWeight: 600,
            color: "#B45309",
            maxWidth: 220,
            cursor: "pointer",
            lineHeight: 1.3,
          }}
        >
          {timer.notice}
        </Typography>
      )}

      {/* Budget spent — ask, don't refuse.
          Work overruns its estimate, and a Start that simply does nothing
          means the work happens off the clock, which costs the timesheet more
          than an honest overrun does. So the overrun is a decision the person
          makes here, and the board shows it as one. */}
      <Dialog
        open={!!budgetAsk}
        onClose={() => setBudgetAsk(null)}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{ sx: { borderRadius: 3, maxWidth: 380 } }}
      >
        <DialogTitle
          sx={{ fontSize: 16.5, fontWeight: 800, color: "#1F2430", pb: 0.5 }}
        >
          Time budget used up
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 13.5, color: "#6B7280" }}>
            {ticket?.ticket_number ? `#${ticket.ticket_number} has ` : "This ticket has "}
            a {budgetAsk?.minutes} min budget and {budgetAsk?.used} min already
            logged. Starting the timer will run over it — the extra time is
            still recorded, and shows on the live board as over budget.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setBudgetAsk(null)}
            sx={{ textTransform: "none", color: "#6B7280", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmOverBudget}
            variant="contained"
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              bgcolor: "#15803D",
              "&:hover": { bgcolor: "#116632" },
            }}
          >
            Start anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TicketTimerButton;
